import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './order-form-button.style.js'
import type { OrderFormButtonState, OrderFormLayout } from './types.js'

export class OrderFormAddedEvent extends Event {
  static type = 'order-form-add' as const

  constructor() {
    super(OrderFormAddedEvent.type)
  }
}

@customElement('csc-order-form-button')
export class CSCOrderFormButton extends LitElement {
  static override styles: CSSResultGroup = [style]

  get fit() {
    if (this.showLabel) return 'fill'
    return this.layout === 'default' ? 'square' : 'fill'
  }

  @property({ attribute: 'state', reflect: true })
  public state: OrderFormButtonState = 'initial'

  @property({ attribute: 'layout' })
  public layout: OrderFormLayout = 'default'

  @property({ type: Boolean, attribute: 'show-label' })
  public showLabel: boolean = false

  public addToBasketCallback = () => {
    this.dispatchEvent(new OrderFormAddedEvent())
  }

  private renderButtonInitial = () => html`
    <cse-button variant="primary" fit="${this.fit}" @click=${this.addToBasketCallback}>
      <cse-icon slot="prefix" icon-role="csc-order-form.initial"></cse-icon>
      ${this.showLabel ? t('button_label_initial', { ns: 'csc-order-form', default: 'Add to cart' }) : nothing}
    </cse-button>`

  private renderButtonPending = () => html`
    <cse-button variant="primary" fit="${this.fit}">
      <cse-loader slot="prefix" animated>
        <cse-icon slot="custom-icon" icon-role="csc-order-form.pending"></cse-icon>
      </cse-loader>
      ${this.showLabel ? t('button_label_pending', { ns: 'csc-order-form', default: 'Updating cart' }) : nothing}
    </cse-button>`

  private renderButtonConfirmed = () => html`
    <cse-button variant="primary" fit="${this.fit}" @click=${this.addToBasketCallback}>
      <cse-icon slot="prefix" icon-role="csc-order-form.confirmed"></cse-icon>
      ${this.showLabel ? t('button_label_confirmed', { ns: 'csc-order-form', default: 'In cart' }) : nothing}
    </cse-button>`

  private renderButtonReorder = () => html`
    <cse-button variant="primary" fit="${this.fit}" @click=${this.addToBasketCallback}>
      <cse-icon slot="prefix" icon-role="csc-order-form.reorder"></cse-icon>
      ${this.showLabel ? t('button_label_reorder', { ns: 'csc-order-form', default: 'Update cart' }) : nothing}
    </cse-button>`

  private renderButtonError = () => html`
    <cse-button variant="primary" fit="${this.fit}" disabled>
      <cse-icon slot="prefix" icon-role="csc-order-form.error"></cse-icon>
      ${this.showLabel ? t('button_label_error', { ns: 'csc-order-form', default: 'Could not add' }) : nothing}
    </cse-button>`

  private renderButton = () => {
    switch (this.state) {
      case 'initial':
        return this.renderButtonInitial()
      case 'pending':
        return this.renderButtonPending()
      case 'confirmed':
        return this.renderButtonConfirmed()
      case 'reorder':
        return this.renderButtonReorder()
      case 'error':
        return this.renderButtonError()
      default:
        return this.renderButtonError()
    }
  }

  override render() {
    return this.renderButton()
  }
}
